import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Link2, Unlink, Search, Download, CheckCircle, XCircle,
  Loader2, ExternalLink, Calendar, MapPin, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/common';

const API_URL = import.meta.env.VITE_API_URL || '';

interface EventbriteStatus {
  connected: boolean;
  user?: { name: string; email: string };
  message: string;
}

interface EventbriteEvent {
  id: string;
  name: { text: string };
  description?: { text: string };
  start: { local: string };
  end?: { local: string };
  venue?: {
    name: string;
    address: {
      city: string;
      country: string;
      localized_address_display: string;
    };
  };
  logo?: { url: string };
  url: string;
  is_free: boolean;
}

interface SearchResults {
  events: EventbriteEvent[];
  pagination: {
    page_number: number;
    page_count: number;
    has_more_items: boolean;
  };
}

export default function Integrations() {
  const [searchParams] = useSearchParams();
  const [eventbriteStatus, setEventbriteStatus] = useState<EventbriteStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('korean');
  const [searchLocation, setSearchLocation] = useState('Los Angeles');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check for OAuth callback messages
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (success === 'eventbrite_connected') {
      setMessage({ type: 'success', text: 'Successfully connected to Eventbrite!' });
    } else if (error) {
      setMessage({ type: 'error', text: `Connection failed: ${error}` });
    }
  }, [searchParams]);

  // Fetch Eventbrite connection status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/eventbrite/status`);
        if (response.ok) {
          const data = await response.json();
          setEventbriteStatus(data);
        }
      } catch (error) {
        console.error('Error fetching status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  // Connect to Eventbrite
  const handleConnect = () => {
    window.location.href = `${API_URL}/api/eventbrite/auth`;
  };

  // Disconnect from Eventbrite
  const handleDisconnect = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eventbrite/disconnect`, {
        method: 'POST',
      });

      if (response.ok) {
        setEventbriteStatus({ connected: false, message: 'Disconnected' });
        setMessage({ type: 'success', text: 'Disconnected from Eventbrite' });
        setSearchResults(null);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to disconnect' });
    }
  };

  // Search Eventbrite events
  const handleSearch = async () => {
    setSearching(true);
    setMessage(null);

    try {
      const params = new URLSearchParams({
        q: searchQuery,
        location: searchLocation,
      });

      const response = await fetch(`${API_URL}/api/eventbrite/search?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
        setSelectedEvents(new Set());
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Search failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Search failed' });
    } finally {
      setSearching(false);
    }
  };

  // Toggle event selection
  const toggleEventSelection = (eventId: string) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(eventId)) {
      newSelected.delete(eventId);
    } else {
      newSelected.add(eventId);
    }
    setSelectedEvents(newSelected);
  };

  // Select all events
  const selectAllEvents = () => {
    if (searchResults?.events) {
      setSelectedEvents(new Set(searchResults.events.map(e => e.id)));
    }
  };

  // Import selected events
  const handleImport = async () => {
    if (selectedEvents.size === 0) {
      setMessage({ type: 'error', text: 'No events selected' });
      return;
    }

    setImporting(true);
    setMessage(null);

    try {
      const eventsToImport = searchResults?.events.filter(e => selectedEvents.has(e.id)) || [];

      const response = await fetch(`${API_URL}/api/eventbrite/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: eventsToImport }),
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: result.message });
        setSelectedEvents(new Set());
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Import failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="px-8 py-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-coral-500" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Integrations
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Connect external services to import events
        </p>
      </div>

      {/* Status Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          {message.text}
        </div>
      )}

      {/* Eventbrite Integration */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden mb-8">
        <div className="p-6 border-b dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-7 h-7 text-orange-500" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 17.08c-.192.268-.503.388-.815.388H6.921c-.312 0-.623-.12-.815-.388-.191-.267-.231-.608-.12-.908l4.163-11.31c.176-.48.72-.48.896 0l4.163 11.31c.111.3.072.641-.12.908h-.194z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Eventbrite
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Search and import Korean events from Eventbrite
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {eventbriteStatus?.connected ? (
                <>
                  <span className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Connected
                  </span>
                  <Button
                    onClick={handleDisconnect}
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button onClick={handleConnect} className="bg-orange-500 hover:bg-orange-600">
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Eventbrite
                </Button>
              )}
            </div>
          </div>

          {eventbriteStatus?.connected && eventbriteStatus.user && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg text-sm">
              <span className="text-gray-500 dark:text-gray-400">Connected as: </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {eventbriteStatus.user.name} ({eventbriteStatus.user.email})
              </span>
            </div>
          )}
        </div>

        {/* Search Section */}
        {eventbriteStatus?.connected && (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Search Events
            </h3>

            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Search Query
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="korean, k-pop, kimchi..."
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  placeholder="Los Angeles, New York..."
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleSearch}
                  disabled={searching}
                  className="bg-coral-500 hover:bg-coral-600"
                >
                  {searching ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Search
                </Button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Found {searchResults.events?.length || 0} events
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={selectAllEvents}
                      variant="outline"
                      size="sm"
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={handleImport}
                      disabled={selectedEvents.size === 0 || importing}
                      className="bg-green-500 hover:bg-green-600"
                      size="sm"
                    >
                      {importing ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Import {selectedEvents.size > 0 ? `(${selectedEvents.size})` : ''}
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {searchResults.events?.map((event) => (
                    <div
                      key={event.id}
                      onClick={() => toggleEventSelection(event.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedEvents.has(event.id)
                          ? 'border-coral-500 bg-coral-50 dark:bg-coral-900/20'
                          : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <div className="flex gap-4">
                        {event.logo?.url && (
                          <img
                            src={event.logo.url}
                            alt=""
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white truncate">
                            {event.name.text}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(event.start.local)}
                          </p>
                          {event.venue && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.venue.address?.city}, {event.venue.address?.country}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {event.is_free && (
                              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs">
                                Free
                              </span>
                            )}
                            <a
                              href={event.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-coral-500 hover:underline flex items-center gap-1"
                            >
                              View on Eventbrite
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            selectedEvents.has(event.id)
                              ? 'border-coral-500 bg-coral-500'
                              : 'border-gray-300 dark:border-slate-600'
                          }`}>
                            {selectedEvents.has(event.id) && (
                              <CheckCircle className="w-4 h-4 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Setup Instructions */}
        {!eventbriteStatus?.connected && (
          <div className="p-6 bg-gray-50 dark:bg-slate-700/50">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">
              Setup Instructions
            </h3>
            <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-2 list-decimal list-inside">
              <li>Go to <a href="https://www.eventbrite.com/platform/api-keys" target="_blank" rel="noopener noreferrer" className="text-coral-500 hover:underline">Eventbrite Developer Portal</a></li>
              <li>Create a new app with name "KoreanHub"</li>
              <li>Set OAuth Redirect URI to: <code className="bg-gray-200 dark:bg-slate-600 px-1 rounded">http://localhost:8787/api/eventbrite/callback</code></li>
              <li>Copy the Client ID and Client Secret to your .dev.vars file</li>
              <li>Restart the dev server and click "Connect Eventbrite"</li>
            </ol>
          </div>
        )}
      </div>

      {/* Other Integrations Placeholder */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Korean Cultural Centers
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Events from official Korean Cultural Centers are automatically synced.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {['KCCLA', 'KCCNY', 'KCCUK', 'KCC Australia', 'KCC Canada', 'KCC Germany'].map((kcc) => (
            <div key={kcc} className="flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-700 dark:text-gray-300">{kcc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
